import React, { useState } from 'react';
import qs from 'qs';
import { useNavigate } from 'react-router-dom';
import type { Account, User } from '@axiom/validation';
import type { ButtonProps } from '@axiom/ui';
import { Button, Loader } from '@axiom/ui';
import { isAhqUser } from '@axiom/utils';

import { ImpersonationAccountPickerModal } from '../ImpersonationAccountPickerModal/ImpersonationAccountPickerModal';
import { ImpersonationClientPickerModal } from '../ImpersonationClientPickerModal/ImpersonationClientPickerModal';
import { ServiceChoiceSuccessModal } from '../ServiceChoiceSuccessModal/ServiceChoiceSuccessModal';
import { AccountApi } from '../../api/account-api';
import { postCreateMqlPassing } from '../../api/hubspot-api';

type PickContact = Pick<Account['contacts'][number], 'email' | 'id'>;

export interface StartEngagementButtonProps {
  account?: Account;
  children: React.ReactNode;
  name: string;
  onLoggedOutSubmissionClick?: () => void;
  pattern: ButtonProps['pattern'];
  user?: User | null;
  variation: ButtonProps['variation'];
}

export const StartEngagementButton = ({
  account,
  children,
  name,
  onLoggedOutSubmissionClick,
  pattern,
  user,
  variation,
}: StartEngagementButtonProps) => {
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hubspotApiInitiated, setHubspotApiInitiated] = useState(false);
  const [collectData, setCollectData] = useState(false);
  const [impersonationAccount, setImpersonationAccount] = useState<
    Account | undefined
  >();
  const [impersonationContact, setImpersonationContact] = useState<
    Account['contacts'][number] | undefined
  >();

  const buildUrl = (url: string, contact: PickContact) => {
    return isAhqUser(user)
      ? `${url}${qs.stringify({ contactId: contact.id, contactEmail: contact.email }, { addQueryPrefix: true })}`
      : url;
  };

  const clearData = () => {
    setCollectData(false);
    setImpersonationContact(undefined);
    setImpersonationAccount(undefined);
    return true;
  };

  const referenceAccount = account || impersonationAccount;
  const referenceContact = user?.contact || impersonationContact;

  const handleNavigateToDirectEngagement = async (
    refAccount: Account,
    refContact: Pick<Account['contacts'][number], 'email' | 'id'>
  ) => {
    if (refAccount.accountType === 'Direct') {
      navigate(buildUrl(`/account/${refAccount.id}/intake`, refContact));
      return clearData();
    }

    /**
     * Unless you know why and when to use .toPromise,
     * DO NOT COPY THIS
     */
    const { data: accountNewEngagementAssignment } =
      await AccountApi.refreshAccountNewEngagementsAssignment(refAccount.id, {
        contactId: refContact.id,
      }).toPromise();

    if (accountNewEngagementAssignment.canSelectSelfService) {
      navigate(
        buildUrl(`/account/${refAccount.id}/service-choice`, refContact)
      );
      return clearData();
    }

    setHubspotApiInitiated(true);
    const data = {
      fields: [
        {
          name: 'email',
          value: refContact.email || '',
        },
        {
          name: 'salesforce_account_owner',
          value: accountNewEngagementAssignment.assignTo,
        },
        {
          name: 'leadsource',
          value: 'AFC - Self-Service',
        },
        {
          name: 'contactId',
          value: refContact.id || '',
        },
      ],
    };
    await postCreateMqlPassing(data);
    setHubspotApiInitiated(false);
    setShowSuccessModal(true);
    return null;
  };

  return (
    // this <div/> prevents BUG FR-5898
    <div>
      {hubspotApiInitiated ? (
        <Loader expand="fullscreen" name="hubspot-loader" />
      ) : null}
      <Button
        name={name}
        onClick={async () => {
          if (!user) {
            onLoggedOutSubmissionClick?.();
          } else if (!referenceAccount || !referenceContact) {
            setCollectData(true);
          } else {
            await handleNavigateToDirectEngagement(referenceAccount, {
              email: referenceContact.email,
              id: referenceContact.id,
            });
          }
        }}
        variation={variation}
        pattern={pattern}
      >
        {children}
      </Button>
      {showSuccessModal ? (
        <ServiceChoiceSuccessModal
          onClose={() => {
            clearData();
            setShowSuccessModal(false);
          }}
        />
      ) : null}
      {collectData ? (
        <div>
          {referenceAccount ? (
            <ImpersonationClientPickerModal
              account={referenceAccount}
              onClose={() => {
                clearData();
              }}
              selectContact={async contact => {
                setImpersonationContact(contact);
                await handleNavigateToDirectEngagement(referenceAccount, {
                  email: contact.user.email,
                  id: contact.id,
                });
              }}
              submitButtonCopy="Start new Engagement"
            />
          ) : (
            <ImpersonationAccountPickerModal
              onClose={() => {
                clearData();
              }}
              selectAccount={chosenAccount => {
                setImpersonationAccount(chosenAccount);
              }}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};
